import type { backendOld } from "~/api";
import { isDefined } from "~/shared/common";
import type { AgentHistoryEvent, Entity } from "~/shared/types";
import { makeStringComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";

const AGENT_HISTORY_PAGINATION_KEY = "agent-history";
const historyEventComparator = makeStringComparator((event: AgentHistoryEvent) => event.created_at, true);

export type State = {
  _duidsToHistoryEvents: Map<string, AgentHistoryEvent>;
};

export const state: State = {
  _duidsToHistoryEvents: new Map(),
};

export type Getters = {
  getHistoryEventByDuidIfLoaded: (duid: string) => AgentHistoryEvent | undefined;
  getLoadedAgentHistoryOrdered: (agentDuid: string) => AgentHistoryEvent[];
  isAgentHistoryLoaded: (agentDuid: string) => boolean;
};

export type Actions = {
  loadAgentHistory: (
    ...args: Parameters<typeof backendOld.agents.getHistory>
  ) => ReturnType<typeof backendOld.agents.getHistory>;
  loadPaginatedAgentHistory: (agentDuid: string) => Promise<number>;
  $createEvent: (event: AgentHistoryEvent) => void;
  $addAgentHistoryEvent: (agentDuid: string, event: AgentHistoryEvent) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getHistoryEventByDuidIfLoaded() {
    return (duid) => this._duidsToHistoryEvents.get(duid);
  },
  isAgentHistoryLoaded() {
    return (agentDuid) => this.$getPaginationState(AGENT_HISTORY_PAGINATION_KEY, agentDuid) !== undefined;
  },
  getLoadedAgentHistoryOrdered() {
    return (agentDuid) => {
      const paginationState = this.$getPaginationState(AGENT_HISTORY_PAGINATION_KEY, agentDuid);
      if (!paginationState) {
        return [];
      }

      return [...paginationState.duids.values()]
        .map((duid) => this.getHistoryEventByDuidIfLoaded(duid))
        .filter(isDefined)
        .sort(historyEventComparator);
    };
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async loadAgentHistory(...args) {
    const response = await this.$backendOld.agents.getHistory(...args);
    (response.data.results as AgentHistoryEvent[]).forEach((event) => {
      this._duidsToHistoryEvents.set(event.duid, event);
    });
    return response;
  },
  async loadPaginatedAgentHistory(agentDuid) {
    const paginationState = this.$getPaginationState(AGENT_HISTORY_PAGINATION_KEY, agentDuid);
    const { data } = await this.loadAgentHistory(agentDuid, paginationState?.duids.size ?? 0);

    if (!paginationState) {
      this.$createPaginationState(AGENT_HISTORY_PAGINATION_KEY, agentDuid, historyEventComparator);
    }
    this.$addEntitiesToPaginationState(AGENT_HISTORY_PAGINATION_KEY, agentDuid, data.results, data.count);

    return data.count;
  },
  $createEvent(event) {
    if (event.agent_duid) {
      this.$addAgentHistoryEvent(event.agent_duid, event);
    }
  },
  $addAgentHistoryEvent(agentDuid, event) {
    this._duidsToHistoryEvents.set(event.duid, event);
    this.$addEntitiesToPaginationState(AGENT_HISTORY_PAGINATION_KEY, agentDuid, [event as unknown as Entity], null);
  },
};

export { actions, getters };
